<template>
  <v-flex md12>
    <kendo-grid
      height="calc(100vh - 145px)"
      :dataSource="dataSource"
      :editable="!isReadOnly && 'inline'"
      :toolbar="!isReadOnly && isMasterClient ? ['create'] : []"
      ref="grid"
      :filterable="true"
      :sortable="true"
      :pageable-always-visible="true"
      :data-source="this.dataSource"
      :columns="columns"
      :detailInit="detailInit"
      @beforeedit="onBeforeEdit"
      @detailexpand="onDetailExpand"
      @detailcollapse="onDetailCollapse"
    >
    </kendo-grid>
  </v-flex>
</template>

<script>
import {getDataItem} from '@/helpers/kendo-helpers'
import {enableExportExcel} from '@/helpers/export-helper'
import {
  checkboxColumn,
  radioButtonColumn,
  readOnlyEditor,
  numericEditor,
} from '@/helpers/kendo-editors'
import {mapGetters} from 'vuex'
import {jsonDropDownEditor} from '@/helpers/kendo-editors'
import axios from 'axios'
import security from '@/mixins/security'

const $ = kendo.jQuery

export default {
  name: 'CcyPair',
  mixins: [security],
  mounted() {
    const grid = this.$refs.grid.kendoWidget()

    enableExportExcel(grid, '/api/config/ccyPair/exportToExcel')

    this.dragAndDropGrid(this.isReadOnly)
  },
  data() {
    return {
      selectedBaseCcy: '',
      selectedForeignCcy: '',
      dataSource: new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        pageSize: 30,
        transport: {
          read: {
            type: 'GET',
            url: '/api/config/ccyPair',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          create: {
            type: 'POST',
            url: '/api/config/ccyPair',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/config/ccyPair',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          destroy: {
            type: 'DELETE',
            url: '/api/config/ccyPair',
            dataType: 'json',
          },
        },
        requestEnd: e => {
          if (e.type == 'update' || e.type == 'create') {
            this.$refs.grid.kendoWidget().dataSource.read()
          }
        },
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            id: 'ccyPairId',
            fields: {
              ccyPairId: {type: 'number', defaultValue: 0},
              baseCcy: {type: 'string', validation: {required: true}},
              isBuy: {type: 'bool', defaultValue: true, editable: true},
              foreignCcy: {type: 'string', validation: {required: true}},
              code: {type: 'string', editable: true},
              isDividingCcy: {type: 'bool', defaultValue: true, editable: this.isMasterClient},
              mtmCcy: {type: 'string', validation: {required: true}},
              isDefault: {type: 'boolean', defaultValue: false, editable: true},
              lagDays: {type: 'number', defaultValue: 0},
              sequence: {type: 'number'},
              canDelete: {type: 'bool', defaultValue: true, editable: false},
              curveId: {type: 'number', defaultValue: -1},
              defaultUnitId: {type: 'number', validation: {required: true}},
              defaultUnit: {type: 'string', validation: {required: true}},
              curveName: {type: 'string'},
              color: {type: 'string', defaultValue: '#FFFFFF'},
              commExpDivisionId: {type: 'number', editable: true},
              divisionName: {type: 'string', defaultValue: ''},
            },
          },
        },
      }),
    }
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    isMasterClient() {
      return this.clientId == 0
    },
    mtmCcyDataSource() {
      let ccyData = [{ccy: this.selectedBaseCcy}, {ccy: this.selectedForeignCcy}]
      return ccyData.filter(x => !/\d/.test(x.ccy))
    },
    columns() {
      return [
        {
          template: '<span class="k-icon k-i-reorder drag-handle" title="Drag to reorder" />',
          attributes: {class: 'drag-handle', style: 'text-align: center'},
          width: 45,
          hidden: this.isReadOnly,
        },
        {
          editable: !this.isReadOnly,
          command: [{name: 'edit'}],
          width: 61,
        },
        checkboxColumn(
          'isBuy',
          {
            title: 'Buy',
            width: 82,
          },
          this.isReadOnly,
        ),
        {
          field: 'foreignCcy',
          title: 'Commodity / Ccy',
          width: 114,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.foreignCcyDropDownEditor,
          template: '#=foreignCcy#',
        },
        {
          field: 'baseCcy',
          title: 'Base Ccy',
          width: 120,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.baseCcyDropDownEditor,
          template: '#=baseCcy#',
        },
        checkboxColumn('isDividingCcy', {
          title: 'Dividing',
          width: 90,
        }),
        {
          field: 'mtmCcy',
          title: 'Mtm Ccy',
          width: 120,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.mtmCcyDropDownEditor,
          template: '#=mtmCcy#',
        },

        {
          field: 'code',
          title: 'Code',
          width: 100,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.codeEditor,
          template: '#=code#',
        },
        {
          field: 'defaultUnit',
          title: 'Default Unit',
          width: 100,
          attributes: {style: 'text-align: left;'},
          editor: function(container, options) {
            const url = `/api/config/ccyPair/getUnit?ccyPairId=${options.model.ccyPairId}`
            return jsonDropDownEditor('unitId', 'unitName', url, {
              change: e => {
                let rowItem = getDataItem(e.sender.element)
                rowItem.set('defaultUnitId', e.sender.value())
                rowItem.set('defaultUnit', e.sender.text())
              },
            }).call(this, container, options)
          },
          template: '#=defaultUnit#',
        },

        radioButtonColumn(
          'isDefault',
          {
            title: 'Default',
            width: 82,
          },
          this.isReadOnly,
        ),
        {
          field: 'lagDays',
          title: 'Lag Days',
          width: 100,
          format: '{0:n0}',
          attributes: {style: 'text-align: right;'},
          headerAttributes: {style: 'text-align: center; vertical-align:middle;'},
          editor: this.lagDaysEditor,
        },
        {
          field: 'curveName',
          title: 'Curve',
          width: 100,
          editor: this.curveDropDownEditor,
        },
        {
          editable: !this.isReadOnly && this.isMasterClient,
          command: [{name: 'destroy', visible: dataItem => dataItem.canDelete}],
          width: 40,
        },
        {
          field: 'commExpDivisionId',
          title: 'Comm.Exp. Division',
          width: 150,
          attributes: {style: 'text-align: left;'},
          editor: (vm =>
            function(container, options) {
              if (options.model.marketId == 1) return ''

              return jsonDropDownEditor('commExpDivisionId', 'divisionName', null, {
                dataSource: {
                  schema: {
                    parse(response) {
                      return response.map(item => {
                        return {
                          commExpDivisionId: item.divisionId,
                          divisionName: item.divisionName,
                        }
                      })
                    },
                  },
                  transport: {
                    read: {
                      url: '/api/config/CcyPair/getDivisions',
                      data: () => ({
                        productContractId: options.model.ccyPairId,
                        clientId: vm.clientId,
                      }),
                      contentType: 'application/json',
                      type: 'GET',
                    },
                  },
                },
              }).call(this, container, options)
            })(this),
          template: '#:divisionName# ',
          hidden: this.isMaster,
        },
        {},
      ].filter(c => c.editable !== false)
    },
  },
  methods: {
    onDetailExpand() {
      this.dragAndDropGrid(true)
    },
    onDetailCollapse() {
      this.dragAndDropGrid(false)
    },
    dragAndDropGrid(hide) {
      let grid = this.$refs.grid.kendoWidget()
      if (hide) {
        let dragAndDrop = grid.table.data('kendoSortable')
        if (dragAndDrop) dragAndDrop.destroy()
        grid.hideColumn(0)
      } else {
        if (this.isReadOnly) return
        grid.showColumn(0)
        grid.table.kendoSortable({
          handler: '.drag-handle',
          filter: 'tr:not(tr[data-role=editable])',
          hint: function(element) {
            var table = $('<table style="width: 600px;" class="k-grid k-widget"></table>')
            table.append(element.clone())
            table.css('opacity', 0.7)

            return table
          },
          cursor: 'move',
          placeholder: function() {
            return $('<tr colspan="4" class="placeholder"></tr>')
          },
          change: async e => {
            const ccyPairGrid = this.$refs.grid.kendoWidget()
            const skip = ccyPairGrid.dataSource.skip(),
              newIndex = e.newIndex + skip,
              selectedDataItem = ccyPairGrid.dataSource.getByUid(e.item.data('uid'))

            ccyPairGrid.dataSource.remove(selectedDataItem)
            ccyPairGrid.dataSource.insert(newIndex, selectedDataItem)

            await axios.post('/api/config/ccyPair/updateSequence', {
              ccyPairId: selectedDataItem.ccyPairId,
              newIndex: e.newIndex,
              clientId: this.clientId,
            })
            await ccyPairGrid.dataSource.read()
          },
        })
      }
    },
    onBeforeEdit({model}) {
      const {foreignCcy, baseCcy} = model
      this.selectedBaseCcy = baseCcy
      this.selectedForeignCcy = /\d/.test(foreignCcy) ? null : foreignCcy
    },
    baseCcyDropDownEditor(container, options) {
      if (!this.isMasterClient) {
        return readOnlyEditor.call(this, container, options)
      }

      let grid = this.$refs.grid.kendoWidget()
      var dataItem = grid.dataItem(container.closest('tr'))

      let textField = 'ccy'
      let valueField = 'ccy'
      let usesValidation = true
      let url = '/api/config/ccyPair/getCcies'

      const defaultOptions = {
        filter: 'contains',
        template: '<span>${' + textField + '}</span>',
        change: e => {
          //const text = e.sender.text().trim()
          const value = e.sender.value()

          dataItem = getDataItem(e.sender.element)
          dataItem.set(options.field, value)

          const foreignCcy = dataItem.foreignCcy
          let mtmCcyDropDown = $('#mtmCcy').getKendoDropDownList()
          let data = foreignCcy != value ? [{ccy: foreignCcy}, {ccy: value}] : [{ccy: value}]
          mtmCcyDropDown.dataSource.data(data.filter(x => !/\d/.test(x.ccy)))
          mtmCcyDropDown.select(0)

          dataItem.set('mtmCcy', mtmCcyDropDown.value())
          //if (text != '') {
          //  dataItem.set(textField, text)
          //}
        },
        dataSource: {
          transport: {
            read: {
              url: url,
              contentType: 'application/json',
              type: 'GET',
            },
          },
          schema: {
            parse(response) {
              return response.filter(x => x.marketId == 1)
            },
          },
        },
      }

      const input = `<input ${usesValidation ? 'required' : ''}
                      id="${options.field}"
                      name="${options.field}"
                      data-text-field="${textField}"
                      data-value-field="${valueField}"
                      data-bind="value: ${textField == valueField ? options.field : valueField}"
                      />`

      $(input)
        .appendTo(container)
        .kendoDropDownList({
          ...defaultOptions,
        })

      if (usesValidation) {
        $(`<span class="k-invalid-msg" data-for="${options.field}"></span>`).appendTo(container)
      }
    },
    foreignCcyDropDownEditor(container, options) {
      if (!this.isMasterClient) {
        return readOnlyEditor.call(this, container, options)
      }

      const isNew = options.model.id === 0
      let textField = 'ccy'
      let valueField = 'ccy'
      let usesValidation = false
      let url = '/api/config/ccyPair/getCcies'

      const defaultOptions = {
        filter: 'contains',
        template: '<span>${' + textField + '}</span>',
        // dataBound: function() {
        //   this.trigger('change')
        // },
        change: function(e) {
          const value = e.sender.value()
          let dataItem = getDataItem(e.sender.element)
          dataItem.set(options.field, value)
          let index = this.selectedIndex
          if (this.options.optionLabel && index > 0) {
            index = index - 1
          }
          if (index < 0) {
            return
          }

          let dropDownItem = this.dataItem(index)
          var isFxMarket = dropDownItem.marketId == 1
          if (isFxMarket) {
            $('#ccyPairCode').hide()
            dataItem.set('code', '')
          } else {
            $('#ccyPairCode').show()
            if (isNew) dataItem.set('code', '')
            dataItem.set('isDividingCcy', false)
          }

          const baseCcy = dataItem.baseCcy

          let data = baseCcy != value ? [{ccy: value}, {ccy: baseCcy}] : [{ccy: value}]
          let mtmCcyDropDown = $('#mtmCcy').getKendoDropDownList()
          mtmCcyDropDown.dataSource.data(data.filter(x => !/\d/.test(x.ccy)))

          let unitDropDown = $('#defaultUnit').getKendoDropDownList()

          if (isNew) {
            const url = `/api/config/ccyPair/getUnitByCcy?ccy=${value}`

            let dataSource = new kendo.data.DataSource({
              transport: {
                read: {
                  type: 'GET',
                  url: url,
                  dataType: 'json',
                },
              },
            })
            dataSource.fetch().then(() => {
              var data = dataSource.data()
              unitDropDown.dataSource.data(data)
              unitDropDown.select(0)
              dataItem.set('defaultUnitId', data[0].defaultUnitId)
            })
          }

          if (!dataItem.isDividingCcy && baseCcy == '') {
            $('#mtmCcy').removeAttr('required')
            mtmCcyDropDown.select(0)
            dataItem.set('mtmCcy', mtmCcyDropDown.value())
            return
          } else {
            $('#mtmCcy').attr('required', 'required')
          }

          mtmCcyDropDown.select(0)
          dataItem.set('mtmCcy', mtmCcyDropDown.value())

          //if (text != '') {
          //  dataItem.set(textField, text)
          //}
        },
        dataSource: {
          transport: {
            read: {
              url: url,
              contentType: 'application/json',
              type: 'GET',
            },
          },
        },
      }

      const input = `<input ${usesValidation ? 'required' : ''}
                      id="${options.field}"
                      name="${options.field}"
                      data-text-field="${textField}"
                      data-value-field="${valueField}"
                      data-bind="value: ${textField == valueField ? options.field : valueField}"
                      />`

      $(input)
        .appendTo(container)
        .kendoDropDownList({
          ...defaultOptions,
        })

      if (usesValidation) {
        $(`<span class="k-invalid-msg" data-for="${options.field}"></span>`).appendTo(container)
      }
    },
    mtmCcyDropDownEditor(container, options) {
      let textField = 'ccy'
      let valueField = 'ccy'
      let usesValidation = true

      const defaultOptions = {
        template: '<span>${' + textField + '}</span>',
        filter: 'contains',
        dataSource: this.mtmCcyDataSource,
      }

      const input = `<input ${usesValidation ? 'required' : ''}
                      id="${options.field}"
                      name="${options.field}"
                      data-text-field="${textField}"
                      data-value-field="${valueField}"
                      data-bind="value: ${options.field}"
                      />`

      $(input)
        .appendTo(container)
        .kendoDropDownList({
          ...defaultOptions,
        })

      if (usesValidation) {
        $(`<span class="k-invalid-msg" data-for="${options.field}"></span>`).appendTo(container)
      }
    },
    codeEditor(container, options) {
      if (!this.isMasterClient || options.model.marketId == 1) {
        return readOnlyEditor.call(this, container, options)
      }
      var input = $(
        '<input class="k-textbox" id="ccyPairCode"  name="' + options.field + '" maxlength="32" />',
      )
      input.appendTo(container)
      return
    },
    lagDaysEditor(container, options) {
      return numericEditor({
        format: 'n0',
        decimals: 0,
        min: 0,
        spinners: false,
        step: 0,
      }).call(this, container, options)
    },
    curveDropDownEditor(container, options) {
      return jsonDropDownEditor('curveId', 'curveName', '/api/config/CcyPair/getCcyCurves')(
        container,
        options,
      )
    },
    counterpartyDropDownEditor(container, options) {
      if (options.model.spreadEconomyValueId > 0) {
        return readOnlyEditor.call(this, container, options)
      }

      let url = `/api/config/CcyPair/getCounterparties?ccyPairId=${
        options.model.ccyPairId
      }&clientId=${options.model.clientId}`
      return jsonDropDownEditor('counterpartyId', 'counterpartyCode', url, {}, true)(
        container,
        options,
      )
    },
    numericDetailEditor(container, options) {
      return numericEditor({
        format: 'n2',
        decimals: 2,
        min: 0,
        spinners: false,
        step: 0,
      }).call(this, container, options)
    },
    detailInit(e) {
      const $container = kendo.jQuery("<div id='detailGrid'/>")
      kendo.ui.progress($container, true)
      const ccyPairId = e.data.ccyPairId

      $container.appendTo(e.detailCell).kendoGrid({
        navigatable: true,
        toolbar: !this.isReadOnly ? ['create'] : '',
        editable: !this.isReadOnly && 'inline',
        dataBound: () => kendo.ui.progress($container, false),
        dataSource: {
          type: 'aspnetmvc-ajax',
          transport: {
            create: {
              type: 'POST',
              url: '/api/config/ccyPair/createDetail',
              dataType: 'json',
            },
            read: {
              type: 'GET',
              url: '/api/config/ccyPair/readDetail',
              data: () => ({ccyPairId: ccyPairId, clientId: this.clientId}),
              dataType: 'json',
            },
            update: {
              type: 'PUT',
              url: '/api/config/ccyPair/updateDetail',
              dataType: 'json',
            },
            destroy: {
              type: 'DELETE',
              url: '/api/config/ccyPair/destroyDetail',
              dataType: 'json',
            },
          },
          schema: {
            data: 'data',
            errors: 'errors',
            total: 'total',
            model: {
              id: 'spreadEconomyValueId',
              fields: {
                spreadEconomyValueId: {type: 'number', validation: {required: true}},
                clientId: {
                  type: 'number',
                  defaultValue: this.clientId,
                  validation: {required: true},
                },
                ccyPairId: {type: 'number', defaultValue: ccyPairId, validation: {required: true}},
                counterpartyId: {type: 'number', validation: {required: true}},
                counterpartyCode: {type: 'string', validation: {required: true}},
                spotSpread: {type: 'number', defaultValue: 0, validation: {required: true}},
                fwdSpreadPerMonth: {type: 'number', defaultValue: 0, validation: {required: true}},
                newSpotSpread: {type: 'number', defaultValue: 0, validation: {required: true}},
              },
            },
          },
          error: function() {
            let grid = this.$refs.grid.kendoWidget()
            grid.one('dataBinding', e => {
              e.preventDefault()
            })
          },
        },
        filterable: true,
        sortable: true,
        pageable: true,
        columns: [
          {
            command: 'edit',
            width: 82,
            attributes: {style: 'text-align: center;'},
            hidden: this.isReadOnly,
          },
          {
            field: 'counterpartyCode',
            title: 'Counterparty',
            width: '132px',
            editor: this.counterpartyDropDownEditor,
          },
          {
            field: 'spotSpread',
            title: 'Spot Spread (bps)',
            width: '182px',
            format: '{0:N1}',
            editor: this.numericDetailEditor,
          },
          {
            field: 'fwdSpreadPerMonth',
            title: 'Spread FWD per Month  (bps)',
            width: '182px',
            format: '{0:N1}',
            editor: this.numericDetailEditor,
          },
          {
            field: 'newSpotSpread',
            title: 'New Spot Spread (bps)',
            width: '182px',
            format: '{0:N1}',
            editor: this.numericDetailEditor,
          },
          {
            command: [{name: 'destroy'}],
            width: 82,
            attributes: {style: 'text-align: center;'},
            hidden: this.isReadOnly,
          },
          {},
        ],
      })
    },
  },
}
</script>
